import { makeRedirectUri } from 'expo-auth-session';
import * as QueryParams from 'expo-auth-session/build/QueryParams';
import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';
import type { Session } from '@supabase/supabase-js';

import { supabase } from './client';

WebBrowser.maybeCompleteAuthSession();

export type OAuthProvider = 'apple' | 'google' | 'github';

export type OAuthSignInResult =
  { type: 'success'; session: Session } | { type: 'cancelled' } | { type: 'redirecting' };

const DEFAULT_NATIVE_AUTH_REDIRECT_URI = 'sixam://auth/callback';
const AUTH_CALLBACK_PATH = 'auth/callback';

export function getAuthRedirectUri() {
  if (Platform.OS === 'web') {
    return makeRedirectUri({ path: AUTH_CALLBACK_PATH });
  }

  return makeRedirectUri({
    native: process.env.EXPO_PUBLIC_AUTH_REDIRECT_URI ?? DEFAULT_NATIVE_AUTH_REDIRECT_URI,
  });
}

/** Complete either Supabase's PKCE code response or its implicit token response. */
export async function completeAuthSessionFromUrl(url: string): Promise<Session> {
  const { params, errorCode } = QueryParams.getQueryParams(url);

  const callbackError = params.error_description ?? params.error ?? errorCode;
  if (callbackError) {
    throw new Error(callbackError);
  }

  if (params.code) {
    const { data, error } = await supabase.auth.exchangeCodeForSession(params.code);
    if (error) {
      throw error;
    }
    if (!data.session) {
      throw new Error('Authentication did not return a session.');
    }
    return data.session;
  }

  const { access_token: accessToken, refresh_token: refreshToken } = params;

  if (accessToken || refreshToken) {
    if (!accessToken || !refreshToken) {
      throw new Error('Authentication returned an incomplete session.');
    }

    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) {
      throw error;
    }
    if (!data.session) {
      throw new Error('Authentication did not return a session.');
    }
    return data.session;
  }

  const { data } = await supabase.auth.getSession();
  if (data.session) {
    return data.session;
  }

  throw new Error('Authentication callback did not contain a session.');
}

export async function signInWithOAuth(provider: OAuthProvider): Promise<OAuthSignInResult> {
  const redirectTo = getAuthRedirectUri();
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo,
      skipBrowserRedirect: Platform.OS !== 'web',
    },
  });

  if (error) {
    throw error;
  }

  if (Platform.OS === 'web') {
    return { type: 'redirecting' };
  }

  if (!data.url) {
    throw new Error('No OAuth URL returned');
  }

  const result = await WebBrowser.openAuthSessionAsync(data.url, redirectTo);

  if (result.type === 'success') {
    return { type: 'success', session: await completeAuthSessionFromUrl(result.url) };
  }

  if (result.type === 'cancel' || result.type === 'dismiss') {
    return { type: 'cancelled' };
  }

  throw new Error(`Could not open the ${provider} sign-in session.`);
}

export function getEmailFieldError(message: string) {
  const normalized = message.toLowerCase();
  if (
    normalized.includes('email') ||
    normalized.includes('identifier') ||
    normalized.includes('user not found')
  ) {
    return { email: message };
  }

  return { password: message };
}

export function getInitials(email: string | undefined) {
  if (!email) {
    return '?';
  }

  const [localPart] = email.split('@');
  const parts = localPart.split(/[._-]/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return localPart.slice(0, 2).toUpperCase();
}
